import * as THREE from 'three';
import { loadGlb } from '../../../assets/loadGlb';
import { nearestInteract, queryInteract, type InteractTarget, type Xyz } from '../../../interact/Interact';
import type { PhysicsWorld } from '../../../physics/PhysicsWorld';
import { setPrompt } from '../../../ui/Prompt';

export type DeepJunglePoint = { x: number; y: number; z: number };
export type DeepJungleArea =
  | 'crash_coast'
  | 'deep_jungle'
  | 'riverlands'
  | 'swamp'
  | 'volcanic'
  | 'underground'
  | 'lost_world';

export type DeepJungleFlags = {
  knowMeridian: boolean;
  bowChip: boolean;
};

export type DeepJungleHook = {
  area?: DeepJungleArea;
  prompt?: string;
  flags: DeepJungleFlags;
};

/** Dressing streams in once the player is through Fig Gate. */
export const DJ_STREAM_Z = -68;
/** Area slug flips inland of the fringe, not at the gate itself. */
export const DJ_AREA_Z = -75;

export const DJ_HUT: DeepJunglePoint = { x: -4, y: 1, z: -110 };

export const DJ_NODES: Record<string, DeepJunglePoint> = {
  'DJ-HUT': DJ_HUT,
  'DJ-HELIX': { x: -4, y: 1.15, z: -107.85 },
  'DJ-RADIO': { x: -2.15, y: 0.78, z: -107.15 },
  'DJ-BOW': { x: -5.75, y: 0.84, z: -107.2 },
};

const GROUND_HALF_Y = 0.5;
const _look = new THREE.Vector3();

type JungleMats = {
  dirt: THREE.MeshStandardMaterial;
  litter: THREE.MeshStandardMaterial;
  moss: THREE.MeshStandardMaterial;
  bark: THREE.MeshStandardMaterial;
  canopy: THREE.MeshStandardMaterial;
  fern: THREE.MeshStandardMaterial;
  understory: THREE.MeshStandardMaterial;
  concrete: THREE.MeshStandardMaterial;
  roof: THREE.MeshStandardMaterial;
  rust: THREE.MeshStandardMaterial;
  paper: THREE.MeshStandardMaterial;
  radio: THREE.MeshStandardMaterial;
  voidMat: THREE.MeshStandardMaterial;
};

function loadMap(url: string, rx: number, ry: number): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

function makeMats(): JungleMats {
  return {
    dirt: new THREE.MeshStandardMaterial({
      color: 0x3e3424,
      map: loadMap('/tex/sand.jpg', 5, 12),
      roughness: 0.97,
      metalness: 0.02,
    }),
    litter: new THREE.MeshStandardMaterial({
      color: 0x4a3a22,
      map: loadMap('/tex/fern.jpg', 8, 9),
      roughness: 0.94,
      metalness: 0.02,
    }),
    moss: new THREE.MeshStandardMaterial({
      color: 0x2f4a28,
      map: loadMap('/tex/fern.jpg', 2, 2),
      roughness: 0.9,
      metalness: 0.02,
    }),
    bark: new THREE.MeshStandardMaterial({
      color: 0x4a3224,
      map: loadMap('/tex/bark.jpg', 1, 4),
      roughness: 0.94,
      metalness: 0.02,
    }),
    canopy: new THREE.MeshStandardMaterial({
      color: 0x1e3218,
      map: loadMap('/tex/fern.jpg', 1, 1),
      roughness: 0.88,
      metalness: 0.02,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.28,
    }),
    fern: new THREE.MeshStandardMaterial({
      color: 0x4a6a34,
      map: loadMap('/tex/fern.jpg', 1, 1),
      roughness: 0.82,
      metalness: 0.02,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.32,
    }),
    understory: new THREE.MeshStandardMaterial({
      color: 0x2a421c,
      map: loadMap('/tex/fern.jpg', 1, 1),
      roughness: 0.86,
      metalness: 0.02,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.3,
    }),
    concrete: new THREE.MeshStandardMaterial({
      color: 0x7a7872,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.92,
      metalness: 0.06,
    }),
    roof: new THREE.MeshStandardMaterial({
      color: 0x3a3834,
      roughness: 0.86,
      metalness: 0.12,
    }),
    rust: new THREE.MeshStandardMaterial({
      color: 0x6a4a32,
      map: loadMap('/tex/wreck.jpg', 1, 1),
      roughness: 0.7,
      metalness: 0.28,
    }),
    paper: new THREE.MeshStandardMaterial({
      color: 0xd8cba8,
      roughness: 0.88,
      metalness: 0.02,
    }),
    radio: new THREE.MeshStandardMaterial({
      color: 0x3a3c38,
      roughness: 0.55,
      metalness: 0.22,
    }),
    voidMat: new THREE.MeshStandardMaterial({
      color: 0x12110f,
      roughness: 1,
      metalness: 0,
    }),
  };
}

export function areaAt(z: number): DeepJungleArea {
  return z < DJ_AREA_Z ? 'deep_jungle' : 'crash_coast';
}

/**
 * Deep Jungle fringe inland of Fig Gate. Crash Coast stays resident;
 * this dressing streams in at z < -68 and the area slug flips at z < -75.
 */
export class DeepJungle {
  streamed = false;
  prompt = '';
  readonly flags: DeepJungleFlags = { knowMeridian: false, bowChip: false };
  private groundReady = false;
  private radio: InteractTarget | null = null;
  private bow: InteractTarget | null = null;
  private helix: InteractTarget | null = null;
  private bowMesh: THREE.Object3D | null = null;
  private craftQueued = false;

  extendGround(physics: PhysicsWorld): void {
    if (this.groundReady) {
      return;
    }
    // Existing Crash Coast extra slab ends at z ≈ -96. Cover z -64…-140.
    physics.addStaticCuboid(32, GROUND_HALF_Y, 38, 0, -GROUND_HALF_Y, -102);
    this.groundReady = true;
  }

  /** Load dressing the first time the player is through Fig Gate. */
  sync(scene: THREE.Scene, physics: PhysicsWorld, z: number): void {
    this.extendGround(physics);
    if (this.streamed || z >= DJ_STREAM_Z) {
      return;
    }
    this.build(scene, physics);
  }

  tick(camera: THREE.Camera, player: Xyz, use: boolean): boolean {
    if (!this.streamed) {
      return false;
    }
    const hit = this.query(camera, player);
    this.applyPrompt(hit);
    if (!use) {
      return hit !== null;
    }
    return this.use(hit);
  }

  forceUse(camera: THREE.Camera, player: Xyz): boolean {
    if (!this.streamed) {
      return false;
    }
    const hit = this.query(camera, player) ?? this.nearest(player);
    const ok = this.use(hit);
    this.applyPrompt(this.query(camera, player) ?? this.nearest(player));
    return ok;
  }

  syncPrompt(camera: THREE.Camera, player: Xyz): void {
    if (!this.streamed) {
      return;
    }
    this.applyPrompt(this.query(camera, player));
  }

  restore(flags: { knowMeridian?: unknown; bowChip?: unknown }): void {
    this.flags.knowMeridian = flags.knowMeridian === true;
    this.flags.bowChip = flags.bowChip === true;
    this.applyCollected();
    this.refreshHelix();
  }

  takeCraft(): boolean {
    const hit = this.craftQueued;
    this.craftQueued = false;
    return hit;
  }

  publish(hook: DeepJungleHook): void {
    hook.flags.knowMeridian = this.flags.knowMeridian;
    hook.flags.bowChip = this.flags.bowChip;
    if (this.prompt) {
      hook.prompt = this.prompt;
    }
  }

  private query(camera: THREE.Camera, player: Xyz) {
    camera.getWorldDirection(_look);
    return queryInteract(player, _look, player, this.targets());
  }

  private nearest(player: Xyz) {
    return nearestInteract(player, this.targets());
  }

  private targets(): InteractTarget[] {
    const list: InteractTarget[] = [];
    if (this.radio) {
      list.push(this.radio);
    }
    if (this.bow) {
      list.push(this.bow);
    }
    if (this.helix) {
      list.push(this.helix);
    }
    return list;
  }

  private use(hit: { target: InteractTarget } | null): boolean {
    const id = hit?.target.id;
    if (id === 'DJ-RADIO' && this.radio?.live) {
      this.flags.knowMeridian = true;
      setPrompt('PROJECT MERIDIAN — the mast still ticks');
      return true;
    }
    if (id === 'DJ-BOW' && this.bow?.live) {
      this.flags.bowChip = true;
      this.bow.live = false;
      if (this.bowMesh) {
        this.bowMesh.visible = false;
      }
      this.refreshHelix();
      return true;
    }
    if (id === 'DJ-HELIX' && this.helix?.live) {
      this.craftQueued = true;
      return true;
    }
    return false;
  }

  private applyPrompt(hit: { target: InteractTarget } | null): void {
    this.prompt = hit?.target.prompt ?? '';
    if (this.prompt) {
      setPrompt(this.prompt);
    }
  }

  private applyCollected(): void {
    if (this.flags.bowChip && this.bow) {
      this.bow.live = false;
      if (this.bowMesh) {
        this.bowMesh.visible = false;
      }
    }
  }

  private build(scene: THREE.Scene, physics: PhysicsWorld): void {
    this.streamed = true;
    const root = new THREE.Group();
    tag(root, 'Deep Jungle');
    root.userData.area = 'deep_jungle';
    scene.add(root);

    const mats = makeMats();
    addFloor(root, mats);
    addLitter(root, mats);
    addMoss(root, mats);
    addPath(root, mats);
    addPalms(root, physics, mats);
    addButtress(root, physics, mats);
    addCanopy(root, mats);
    addUnderstory(root, mats);
    addFerns(root, mats);
    addLogs(root, mats);
    addHut(root, physics, mats);
    this.radio = addRadio(root, mats);
    this.bow = addBowSchematic(root, mats);
    this.helix = addHelix();
    this.bowMesh = root.getObjectByName('DJ-BOW') ?? null;
    this.applyCollected();
    this.refreshHelix();
  }

  private refreshHelix(): void {
    if (!this.helix) {
      return;
    }
    this.helix.live = this.flags.bowChip;
    this.helix.prompt = 'E — Craft bow';
  }
}

function addFloor(root: THREE.Group, mats: JungleMats): void {
  const geo = new THREE.PlaneGeometry(72, 78, 28, 30);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i) - 4;
      const z = pos.getZ(i) - 104;
      let y = Math.sin(x * 0.16 + z * 0.09) * 0.08 + Math.sin(x * 0.37 - z * 0.21) * 0.035;
      const near = jungleClearance(x, z);
      if (near < 7) {
        y *= (near / 7) * 0.12;
      }
      pos.setY(i, 0.02 + y);
    }
    pos.needsUpdate = true;
  }
  geo.computeVertexNormals();
  const floor = new THREE.Mesh(geo, mats.dirt);
  floor.position.set(-4, 0, -104);
  floor.receiveShadow = true;
  tag(floor, 'DJ-FLOOR');
  root.add(floor);
}

function addLitter(root: THREE.Group, mats: JungleMats): void {
  const sheet = new THREE.Mesh(new THREE.PlaneGeometry(58, 64), mats.litter);
  sheet.rotation.x = -Math.PI / 2;
  sheet.position.set(-4, 0.028, -104);
  sheet.receiveShadow = true;
  tag(sheet, 'DJ-LITTER');
  root.add(sheet);

  const disks: [number, number, number][] = [
    [-16, -82, 4.2],
    [10, -88, 3.6],
    [-18, -108, 4.8],
    [12, -116, 3.9],
    [-14, -128, 4.4],
    [8, -76, 3.2],
    [-22, -96, 3.5],
    [16, -104, 3.8],
  ];
  for (let i = 0; i < disks.length; i++) {
    const d = disks[i];
    if (!d) {
      continue;
    }
    const [x, z, r] = d;
    const disk = new THREE.Mesh(new THREE.CircleGeometry(r, 14), mats.litter);
    disk.rotation.x = -Math.PI / 2;
    disk.position.set(x, 0.034, z);
    disk.receiveShadow = true;
    tag(disk, `DJ-LITTER-${String(i + 1).padStart(2, '0')}`);
    root.add(disk);
  }
}

function addMoss(root: THREE.Group, mats: JungleMats): void {
  const patches: [number, number, number][] = [
    [-14, -78, 2.4],
    [8, -84, 1.9],
    [-20, -94, 2.8],
    [14, -100, 2.2],
    [-16, -114, 2.6],
    [10, -122, 2.1],
    [-24, -86, 1.7],
    [18, -112, 2.3],
    [-12, -130, 2.0],
    [6, -92, 1.6],
  ];
  for (let i = 0; i < patches.length; i++) {
    const p = patches[i];
    if (!p) {
      continue;
    }
    const [x, z, r] = p;
    const pad = new THREE.Mesh(new THREE.CircleGeometry(r, 12), mats.moss);
    pad.rotation.x = -Math.PI / 2;
    pad.position.set(x, 0.04, z);
    pad.receiveShadow = true;
    tag(pad, `DJ-MOSS-${String(i + 1).padStart(2, '0')}`);
    root.add(pad);
  }
}

function addPath(root: THREE.Group, mats: JungleMats): void {
  const path = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 44), mats.dirt);
  path.rotation.x = -Math.PI / 2;
  path.rotation.z = 0.09;
  path.position.set(-6.1, 0.03, -90);
  path.receiveShadow = true;
  tag(path, 'DJ-PATH');
  root.add(path);
}

function addPalms(root: THREE.Group, physics: PhysicsWorld, mats: JungleMats): void {
  const trees: [number, number, number][] = [
    [-18, -74, 8.4],
    [-22, -80, 7.6],
    [-16, -86, 8.8],
    [-24, -92, 7.9],
    [-20, -98, 8.5],
    [-26, -106, 7.4],
    [-18, -112, 8.2],
    [-22, -120, 7.7],
    [-14, -126, 8.6],
    [-28, -128, 7.3],
    [10, -74, 7.8],
    [14, -82, 8.3],
    [8, -88, 7.5],
    [16, -94, 8.7],
    [12, -102, 7.9],
    [18, -110, 8.4],
    [10, -116, 7.6],
    [15, -124, 8.1],
    [6, -130, 7.8],
    [-8, -132, 8.5],
    [2, -78, 7.2],
    [-12, -96, 8.0],
    [4, -108, 7.4],
    [-10, -118, 8.3],
    [20, -86, 7.7],
    [-30, -88, 8.1],
    [22, -118, 7.5],
    [-6, -84, 6.8],
    [1, -122, 8.0],
    [-15, -108, 7.3],
    [-32, -76, 9.2],
    [-34, -94, 8.6],
    [-36, -112, 9.0],
    [-32, -124, 8.4],
    [24, -76, 8.8],
    [26, -96, 9.1],
    [28, -114, 8.5],
    [24, -128, 8.9],
    [-28, -136, 7.8],
    [18, -136, 8.2],
    [-20, -70, 7.6],
    [16, -70, 7.4],
    [-38, -84, 8.7],
    [30, -88, 8.0],
    [-34, -132, 8.3],
    [22, -140, 7.9],
  ];

  const count = trees.length;
  const trunks = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.22, 0.42, 1, 8), mats.bark, count);
  trunks.name = 'DJ-PALM-TRUNKS';
  trunks.castShadow = true;
  trunks.receiveShadow = true;
  root.add(trunks);

  const frondsPer = 7;
  const fronds = new THREE.InstancedMesh(new THREE.PlaneGeometry(2.8, 1.25), mats.canopy, count * frondsPer);
  fronds.name = 'DJ-PALM-FRONDS';
  root.add(fronds);

  const dummy = new THREE.Object3D();
  let fi = 0;
  for (let i = 0; i < count; i++) {
    const tree = trees[i];
    if (!tree) {
      continue;
    }
    const [x, z, h] = tree;
    dummy.position.set(x, h * 0.5, z);
    dummy.rotation.set(0.05, x * 0.22, 0.03);
    dummy.scale.set(1, h, 1);
    dummy.updateMatrix();
    trunks.setMatrixAt(i, dummy.matrix);
    physics.addStaticCylinder(h * 0.5, 0.28, x, h * 0.5, z);

    for (let f = 0; f < frondsPer; f++) {
      const yaw = (f / frondsPer) * Math.PI * 2 + i * 0.17;
      dummy.position.set(x + Math.sin(yaw) * 0.38, h - 0.12, z + Math.cos(yaw) * 0.38);
      dummy.rotation.set(0.92, yaw, 0.12);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      fronds.setMatrixAt(fi, dummy.matrix);
      fi += 1;
    }
  }
  trunks.instanceMatrix.needsUpdate = true;
  fronds.instanceMatrix.needsUpdate = true;
  trunks.computeBoundingSphere();
  fronds.computeBoundingSphere();
  void dressJunglePalms(root, trunks, fronds, trees);
}

const PALM_GLB = '/models/veg/veg_palm.glb';
const PALM_CANON_H = 7.4;
const HUT_GLB = '/models/bld/bld_meridian_hut.glb';

async function dressJunglePalms(
  root: THREE.Group,
  trunks: THREE.InstancedMesh,
  fronds: THREE.InstancedMesh,
  trees: [number, number, number][],
): Promise<void> {
  let proto: THREE.Group;
  try {
    proto = await loadGlb(PALM_GLB);
  } catch {
    return;
  }
  proto.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.castShadow = true;
      obj.receiveShadow = true;
    }
  });
  const dressed = new THREE.Group();
  dressed.name = 'DJ-PALM-GLB';
  for (const tree of trees) {
    const [x, z, h] = tree;
    const clone = proto.clone(true);
    clone.position.set(x, 0, z);
    clone.scale.setScalar(h / PALM_CANON_H);
    clone.rotation.y = x * 0.22;
    dressed.add(clone);
  }
  root.add(dressed);
  trunks.visible = false;
  fronds.visible = false;
}

function addCanopy(root: THREE.Group, mats: JungleMats): void {
  const card = new THREE.PlaneGeometry(9.2, 7.4);
  const spots: [number, number, number][] = [
    [-10, 8.2, -78],
    [2, 8.6, -84],
    [-6, 8.4, -92],
    [6, 8.8, -98],
    [-12, 8.3, -106],
    [0, 8.7, -112],
    [8, 8.5, -118],
    [-8, 8.9, -124],
    [4, 8.4, -130],
    [-14, 8.1, -88],
    [10, 8.6, -108],
    [-22, 8.8, -96],
    [18, 8.5, -90],
    [-26, 8.7, -118],
    [20, 8.9, -126],
    [-18, 8.4, -136],
  ];
  const group = new THREE.Group();
  tag(group, 'DJ-CANOPY');
  for (let i = 0; i < spots.length; i++) {
    const spot = spots[i];
    if (!spot) {
      continue;
    }
    const [x, y, z] = spot;
    const mesh = new THREE.Mesh(card, mats.canopy);
    mesh.position.set(x, y, z);
    mesh.rotation.set(-0.62, i * 0.28, 0.06);
    group.add(mesh);
  }
  root.add(group);
}

function addFerns(root: THREE.Group, mats: JungleMats): void {
  const clumps: [number, number][] = [
    [-11, -76],
    [3, -80],
    [-9, -88],
    [5, -94],
    [-13, -100],
    [7, -106],
    [-7, -114],
    [3, -120],
    [-11, -126],
    [6, -132],
    [-19, -78],
    [11, -86],
    [-17, -102],
    [13, -110],
    [-21, -118],
    [9, -128],
    [-25, -90],
    [17, -98],
    [-15, -136],
    [19, -132],
  ];
  const leaf = new THREE.PlaneGeometry(1.2, 1.0);
  for (let i = 0; i < clumps.length; i++) {
    const c = clumps[i];
    if (!c) {
      continue;
    }
    const g = new THREE.Group();
    tag(g, `DJ-FERN-${String(i + 1).padStart(2, '0')}`);
    g.position.set(c[0], 0.44, c[1]);
    for (let k = 0; k < 3; k++) {
      const card = new THREE.Mesh(leaf, mats.fern);
      card.position.set((k - 1) * 0.2, 0, (k % 2) * 0.1);
      card.rotation.y = k * 0.85 + i * 0.18;
      g.add(card);
    }
    root.add(g);
  }
}

function addButtress(root: THREE.Group, physics: PhysicsWorld, mats: JungleMats): void {
  const figs: [number, number, number][] = [
    [-24, -82, 9.4],
    [20, -92, 9.8],
    [-30, -108, 10.2],
    [26, -122, 9.6],
  ];
  for (let i = 0; i < figs.length; i++) {
    const fig = figs[i];
    if (!fig) {
      continue;
    }
    const [x, z, h] = fig;
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.82, h, 8), mats.bark);
    trunk.position.set(x, h * 0.5, z);
    trunk.castShadow = true;
    trunk.receiveShadow = true;
    tag(trunk, `DJ-FIG-${String(i + 1).padStart(2, '0')}`);
    root.add(trunk);
    physics.addStaticCylinder(h * 0.5, 0.62, x, h * 0.5, z);

    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI * 2 + i * 0.2;
      const flare = new THREE.Mesh(new THREE.BoxGeometry(0.18, h * 0.42, 0.72), mats.bark);
      flare.position.set(x + Math.cos(a) * 0.55, h * 0.18, z + Math.sin(a) * 0.55);
      flare.rotation.y = a;
      flare.castShadow = true;
      root.add(flare);
    }
  }
}

function addUnderstory(root: THREE.Group, mats: JungleMats): void {
  const spots: [number, number][] = [
    [-13, -80],
    [7, -86],
    [-17, -92],
    [11, -98],
    [-19, -106],
    [13, -112],
    [-15, -120],
    [9, -126],
    [-23, -100],
    [17, -108],
    [-21, -130],
    [15, -78],
    [-27, -88],
    [21, -104],
    [-11, -136],
    [19, -120],
  ];
  const count = spots.length;
  const cardsPer = 4;
  const cards = new THREE.InstancedMesh(new THREE.PlaneGeometry(1.05, 1.35), mats.understory, count * cardsPer);
  cards.name = 'DJ-UNDERSTORY';
  root.add(cards);

  const dummy = new THREE.Object3D();
  let fi = 0;
  for (let i = 0; i < count; i++) {
    const s = spots[i];
    if (!s) {
      continue;
    }
    const [x, z] = s;
    for (let k = 0; k < cardsPer; k++) {
      const yaw = (k / cardsPer) * Math.PI + i * 0.13;
      dummy.position.set(x + Math.sin(yaw) * 0.18, 0.62, z + Math.cos(yaw) * 0.18);
      dummy.rotation.set(0.08, yaw, 0.04);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      cards.setMatrixAt(fi, dummy.matrix);
      fi += 1;
    }
  }
  cards.instanceMatrix.needsUpdate = true;
  cards.computeBoundingSphere();
}

function addLogs(root: THREE.Group, mats: JungleMats): void {
  const logs: [number, number, number, number][] = [
    [-18, -90, 2.4, 0.7],
    [14, -106, 2.8, -0.5],
    [-22, -122, 2.2, 1.1],
    [18, -84, 2.6, 0.3],
  ];
  for (let i = 0; i < logs.length; i++) {
    const l = logs[i];
    if (!l) {
      continue;
    }
    const [x, z, len, yaw] = l;
    const log = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, len, 7), mats.bark);
    log.rotation.z = Math.PI / 2;
    log.rotation.y = yaw;
    log.position.set(x, 0.18, z);
    log.castShadow = true;
    log.receiveShadow = true;
    tag(log, `DJ-LOG-${String(i + 1).padStart(2, '0')}`);
    root.add(log);
  }
}

function jungleClearance(x: number, z: number): number {
  const pts: [number, number][] = [
    [-8, -70],
    [-6.1, -90],
    [-4, -110],
    [0, -90],
    [0, -100],
  ];
  let best = Infinity;
  for (let i = 0; i < 2; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (a && b) {
      best = Math.min(best, distToSeg(x, z, a[0], a[1], b[0], b[1]));
    }
  }
  best = Math.min(best, Math.hypot(x - 0, z + 90));
  best = Math.min(best, Math.hypot(x - 0, z + 100));
  best = Math.min(best, Math.hypot(x + 4, z + 110));
  return best;
}

function distToSeg(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax;
  const dz = bz - az;
  const len = dx * dx + dz * dz;
  const t = len <= 1e-6 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len));
  return Math.hypot(ax + dx * t - px, az + dz * t - pz);
}

function addHut(root: THREE.Group, physics: PhysicsWorld, mats: JungleMats): void {
  const hut = new THREE.Group();
  tag(hut, 'Station Meridian');
  hut.position.set(DJ_HUT.x, 0, DJ_HUT.z);
  root.add(hut);

  const body = new THREE.Mesh(new THREE.BoxGeometry(5, 3, 4.2), mats.concrete);
  body.position.y = 1.5;
  body.castShadow = true;
  body.receiveShadow = true;
  tag(body, 'DJ-HUT');
  hut.add(body);

  const roof = new THREE.Mesh(new THREE.BoxGeometry(5.5, 0.18, 4.7), mats.roof);
  roof.position.y = 3.12;
  roof.castShadow = true;
  hut.add(roof);

  const door = new THREE.Mesh(new THREE.BoxGeometry(1.25, 2.15, 0.42), mats.voidMat);
  door.position.set(0, 1.15, 2.12);
  tag(door, 'DJ-HELIX');
  hut.add(door);

  const plaque = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.28, 0.06), mats.rust);
  plaque.position.set(0, 2.45, 2.14);
  hut.add(plaque);

  const dish = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.08, 10), mats.rust);
  dish.position.set(1.4, 3.42, -0.4);
  dish.rotation.x = 0.7;
  hut.add(dish);
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.11, 14.5, 7), mats.rust);
  mast.position.set(1.6, 7.4, -0.55);
  hut.add(mast);
  const yard = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.12, 2.4), mats.rust);
  yard.position.set(1.6, 13.6, -0.55);
  hut.add(yard);
  const lamp = new THREE.PointLight(0xc8a060, 1.6, 28, 1.6);
  lamp.position.set(1.6, 13.2, -0.55);
  lamp.name = 'DJ-MAST-LAMP';
  hut.add(lamp);

  physics.addStaticCuboid(2.5, 1.5, 2.1, DJ_HUT.x, 1.5, DJ_HUT.z);
  void dressMeridianHut(hut, mats);
}

/** Swap the box hut for the authored Station Meridian GLB. Keeps primitives if the file is missing. */
async function dressMeridianHut(hut: THREE.Group, mats: JungleMats): Promise<void> {
  try {
    const hero = await loadGlb(HUT_GLB);
    hero.name = 'DJ-HUT-HERO';
    const rock = mats.concrete.map;
    const wreck = mats.rust.map;
    hero.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      obj.castShadow = true;
      obj.receiveShadow = true;
      const slots = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of slots) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          continue;
        }
        const slug = mat.name.toLowerCase();
        if (rock && (slug.includes('concrete') || slug.includes('moss'))) {
          mat.map = rock;
          mat.needsUpdate = true;
        }
        if (wreck && (slug.includes('rust') || slug.includes('roof') || slug.includes('enamel'))) {
          mat.map = wreck;
          mat.needsUpdate = true;
        }
      }
    });
    for (const child of [...hut.children]) {
      child.visible = false;
    }
    hut.add(hero);
  } catch {
    /* keep box hut */
  }
}

function addRadio(root: THREE.Group, mats: JungleMats): InteractTarget {
  const pos = DJ_NODES['DJ-RADIO'] ?? { x: -2.15, y: 0.78, z: -107.15 };
  const crate = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.42, 0.55), mats.rust);
  crate.position.set(pos.x, 0.22, pos.z + 0.02);
  crate.castShadow = true;
  crate.receiveShadow = true;
  tag(crate, 'DJ-RADIO-CRATE');
  root.add(crate);

  const box = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.22, 0.28), mats.radio);
  box.position.set(pos.x, pos.y, pos.z);
  box.castShadow = true;
  tag(box, 'DJ-RADIO');
  root.add(box);

  const dial = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.04, 8), mats.rust);
  dial.rotation.x = Math.PI / 2;
  dial.position.set(pos.x, pos.y + 0.02, pos.z + 0.14);
  root.add(dial);

  return {
    id: 'DJ-RADIO',
    prompt: 'E — Listen — Station Meridian',
    position: { ...pos },
    radius: 0.55,
    live: true,
  };
}

function addHelix(): InteractTarget {
  const pos = DJ_NODES['DJ-HELIX'] ?? { x: -4, y: 1.15, z: -107.85 };
  return {
    id: 'DJ-HELIX',
    prompt: 'E — Craft bow',
    position: { ...pos },
    radius: 0.75,
    live: false,
  };
}

function addBowSchematic(root: THREE.Group, mats: JungleMats): InteractTarget {
  const pos = DJ_NODES['DJ-BOW'] ?? { x: -5.75, y: 0.84, z: -107.2 };
  const crate = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.38, 0.5), mats.concrete);
  crate.position.set(pos.x, 0.2, pos.z);
  crate.castShadow = true;
  crate.receiveShadow = true;
  tag(crate, 'DJ-BOW-CRATE');
  root.add(crate);

  const paper = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.012, 0.4), mats.paper);
  paper.position.set(pos.x, pos.y, pos.z);
  paper.rotation.y = 0.28;
  tag(paper, 'DJ-BOW');
  root.add(paper);

  return {
    id: 'DJ-BOW',
    prompt: 'E — Bow schematic',
    position: { ...pos },
    radius: 0.5,
    live: true,
  };
}

function tag(obj: THREE.Object3D, id: string): void {
  obj.name = id;
  obj.userData.id = id;
}
